import { z } from "zod";
import {
  type StoredCard,
  CardScheme,
  StoredCardStatus,
  CardTokenType,
  PaymentMethod,
} from "@/types";
import {
  decodeCardScheme,
  decodeStoredCardStatus,
  decodeCardTokenType,
  decodePaymentMethod,
} from "@/lib/codec";

export const JerkPlanStepSchema = z.enum([
  "payment_options_selection",
  "evaluate_order_credit",
  "reattempt_evaluate_order_credit",
  "confirmation",
  "confirmation_instalment",
  "confirmation_pay_now",
  "risk_consult_response_handling",
  "risk_consult_response_handling_3rd_fraud_check",
  "charge_down_payment",
  "charge_pay_now_payment",
  "finish",
]);

export type JerkPlanStep = z.infer<typeof JerkPlanStepSchema>;

// Helper  for payment type
export const JerkPaymentTypeSchema = z.enum(["down_payment", "repayment"]);

export type JerkPaymentType = z.infer<typeof JerkPaymentTypeSchema>;

export const JerkPlanOptionTypeSchema = z.enum([
  "PAY_BY_INSTALMENTS",
  "PAY_NOW",
  "PAY_NEXT_MONTH",
  "PAY_BY_LATER",
  "PAY_BY_FINANCE",
]);

export type JerkPlanOptionType = z.infer<typeof JerkPlanOptionTypeSchema>;

// Helper  for currency objects
export const JerkCurrencyCodeSchema = z.enum([
  "SAR",
  "AED",
  "KWD",
  "QAR",
  "BHD",
]);

export const JerkCurrencyAmountSchema = z
  .object({
    amount: z.number(),
    currency: JerkCurrencyCodeSchema,
  })
  .transform((origin) => ({
    value: origin.amount,
    unit: origin.currency,
  }));

export type JerkCurrencyAmount = z.infer<typeof JerkCurrencyAmountSchema>;

// Helper  for customer service fee
export const JerkCustomerServiceFeeSchema = z.object({
  fixedServiceFee: JerkCurrencyAmountSchema,
  variableServiceFee: JerkCurrencyAmountSchema,
  vatFixedServiceFee: JerkCurrencyAmountSchema,
  vatVariableServiceFee: JerkCurrencyAmountSchema,
});

export type JerkCustomerServiceFee = z.infer<
  typeof JerkCustomerServiceFeeSchema
>;

export const JerkDownpaymentDetailSchema = z.preprocess(
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  (origin: any) => {
    const out = {
      ...origin,
      amount: {
        amount: origin.amount,
        currency: origin.currency,
      },
    };

    delete out.currency;
    return out;
  },
  z.object({
    amount: JerkCurrencyAmountSchema,
    type: JerkPaymentTypeSchema,
    customerServiceFee: JerkCustomerServiceFeeSchema,
  }),
);

export type JerkDownpaymentDetail = z.infer<typeof JerkDownpaymentDetailSchema>;

// Helper  for repayment details
export const JerkRepaymentDetailSchema = z.object({
  amount: JerkCurrencyAmountSchema,
  customerServiceFeeRate: z.number().nullable(),
  cartAmount: JerkCurrencyAmountSchema,
  dueDate: z.iso.datetime({ offset: true }).pipe(z.coerce.date()),
  type: JerkPaymentTypeSchema,
  originalDueDate: z.iso.datetime({ offset: true }).pipe(z.coerce.date()),
  customerServiceFee: JerkCustomerServiceFeeSchema,
});

export type JerkRepaymentDetail = z.infer<typeof JerkCustomerServiceFeeSchema>;

export const JerkPlanOptionSchema = z.preprocess(
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  (origin: any) => {
    const out = {
      ...origin,
      planOptionType: origin.paymentType,
    };

    delete out.paymentType;

    return out;
  },
  z.object({
    planOptionType: JerkPlanOptionTypeSchema,
    descriptionTitle: z.string(),
    eligibility: z.object({ status: z.boolean(), reason: z.string() }),
    descriptionBody: z.object({
      descriptionBodyDownPayment: JerkCurrencyAmountSchema,
      // descriptionBodyRepayment: CurrencyAmountSchema.optional(),
      descriptionBodyRepaymentDetails: z
        .array(JerkRepaymentDetailSchema)
        .optional(),
    }),
    instalments: z.number().optional(),
    preSelect: z.boolean(),
  }),
);

export type JerkPlanOption = z.infer<typeof JerkPlanOptionSchema>;

export const JerkPaymentOptionsSchema = z.array(JerkPlanOptionSchema);

export const JerkPlanDetailSchema = z.object({
  walletDownpayment: JerkCurrencyAmountSchema,
  rewardDownpayment: JerkCurrencyAmountSchema,
  downpayment: JerkDownpaymentDetailSchema,
  repayments: z.array(JerkRepaymentDetailSchema),
  customerServiceFee: JerkCurrencyAmountSchema,
  isWaivedOff: z.boolean(),
  waiveOffCustomerServiceFee: JerkCurrencyAmountSchema,
  isAmountBelowCsfThreshold: z.boolean(),
});

export type JerkPlanDetail = z.infer<typeof JerkPlanDetailSchema>;

////////////////////////////////
// SESSION
////

export const JerkPhoneNumberSchema = z.object({
  countryCode: z.number(),
  nationalNumber: z.string(),
  internationalNumber: z.string(),
});

export type JerkPhoneNumber = z.infer<typeof JerkPhoneNumberSchema>;

// export const MerchantCategory = z.object({
//   name: z.string(),
//   ar_name: z.string(),
//   cat_id: z.number(),
//   display_order: z.number(),
//   is_visible: z.boolean(),
//   logo: z.string().nullable(),
//   ar_logo: z.string().nullable(),
// });

// export type MerchantCategory = z.infer<typeof MerchantCategory>;

// export const PayNowConfigs = z.object({
//   pay_now_cashback_percentage: z.number(),
//   pay_now_cashback_value: z.number(),
//   pay_now_cvv_disabled: z.boolean(),
// });

// export type PayNowConfigs = z.infer<typeof PayNowConfigs>;

export const JerkSessionDataSchema = z.object({
  merchantName: z.string(),
  // platform: z.string(),
  totalAmount: JerkCurrencyAmountSchema,
  cancelUrl: z.url(),
  phoneNumber: JerkPhoneNumberSchema.nullable().optional(),
  paymentType: JerkPlanOptionTypeSchema,
  instalments: z.number().nullable(),
  locale: z.string(),
  token: z.string(),
  countryCode: z.string().length(2),
  merchantLogo: z.string(),
  merchantId: z.uuid(),
  merchantCountry: z.string().length(2),
  // merchant_categories: z.array(MerchantCategory),
  // order_shipping_city: z.string(),
  // order_shipping_country: z.string().length(2),
  // is_namshi: z.boolean(),
  seonEnabled: z.boolean(),
  // display_service_fees: z.boolean(),
  fromPaymentLink: z.boolean(),
  createdAt: z.string(),
  // single_checkout_enabled: z.boolean(),
  // single_checkout_revamp_enabled: z.boolean(),
  // autofill_phone_number: z.boolean(),
  paymentPlan: JerkPlanDetailSchema.nullable(),
  orderId: z.string(),
  // order_platform: z.string(),
  approvalExpiresAt: z.string(),
  lastPaymentMethod: z.string().nullable(), // Define specific schema when structure is known
  // sendOtpAtHandshakeEnabled: z.boolean(),
  // handshake_otp_result: z.unknown().nullable(), // Define specific schema when structure is known
  // leavePhoneNumberEmptyForNewCustomers: z.boolean(),
  isLavenderDesignEnabled: z.boolean(),
  // pay_now_configs: PayNowConfigs,
});

export type JerkSessionData = z.infer<typeof JerkSessionDataSchema>;

////////////////////////////////
// CARDS
////

// Helper for card scheme
// BE sends the scheme as a free-form string with inconsistent casing
// (e.g. both "Mastercard" and "mastercard") and values beyond the ones the UI
// maps (e.g. "mada", "knet"). This is intentionally permissive: a single
// unrecognised scheme must never fail the whole cards response and leave the
// payment page stuck. The transform below normalises casing via
// `decodeCardScheme` and falls back to `CardScheme.UNKNOWN` for anything unmapped.
export const JerkCardSchemeSchema = z.string();

export type JerkCardScheme = z.infer<typeof JerkCardSchemeSchema>;

// Helper for card status
export const JerkCardStatusSchema = z.enum(["active", "inactive", "expired"]);

export type JerkCardStatus = z.infer<typeof JerkCardStatusSchema>;

// Helper for token type
export const JerkTokenTypeSchema = z.enum(["card", "prepaid"]);

export type JerkTokenType = z.infer<typeof JerkTokenTypeSchema>;

export const JerkCardsSchema = z.object({
  id: z.number(),
  scheme: JerkCardSchemeSchema,
  last4: z.string().length(4),
  expiryMonth: z.number().min(1).max(12),
  expiryYear: z.number(),
  status: JerkCardStatusSchema,
  paymentMethod: z.string(),
  isExpiredSoon: z.object({
    PAY_NEXT_MONTH: z.boolean().catch(false),
    PAY_BY_INSTALMENTS: z.boolean().catch(false),
    PAY_NOW: z.boolean().catch(false),
    PAY_BY_LATER: z.boolean().catch(false),
    PAY_BY_FINANCE: z.boolean().catch(false),
  }),
  schemeLocal: z.string().nullable(),
  isDebit: z.boolean(),
  isPrepaid: z.boolean(),
  isAutoCharge: z.boolean(),
  isBinBlacklisted: z.boolean(),
  bin: z.string(),
  isDisabledCvv: z.boolean(),
  cardPriority: z.number(),
  tokenType: JerkTokenTypeSchema.nullable(),
  fingerprint: z.string().optional(),
});

export type JerkCard = z.infer<typeof JerkCardsSchema>;

export const JerkCardsResponseSchema = z
  .object({
    cards: z.array(JerkCardsSchema),
    metaData: z.object({
      applePayPreference: z.boolean(),
    }),
  })
  .transform(
    (
      data,
    ): {
      cards: StoredCard[];
      metaData: {
        applePayPreference: boolean;
      };
    } => ({
      cards: data.cards.map(
        (card): StoredCard => ({
          id: card.id,
          scheme:
            decodeCardScheme(card.scheme.toLowerCase()) ?? CardScheme.UNKNOWN,
          schemeLocal:
            decodeCardScheme((card.schemeLocal ?? "").toLowerCase()) ??
            CardScheme.UNKNOWN,
          last4: card.last4,
          expiryMonth: card.expiryMonth,
          expiryYear: card.expiryYear,
          status:
            decodeStoredCardStatus(card.status) ?? StoredCardStatus.UNKNOWN,
          paymentMethod:
            decodePaymentMethod(card.paymentMethod) ??
            PaymentMethod.CREDIT_CARD,
          isExpiredSoon: new Set(),
          isDebit: card.isDebit,
          isPrepaid: card.isPrepaid,
          isAutoCharge: card.isAutoCharge,
          isBinBlacklisted: card.isBinBlacklisted,
          bin: card.bin,
          isDisabledCvv: card.isDisabledCvv,
          cardPriority: card.cardPriority,
          tokenType:
            decodeCardTokenType(card.tokenType ?? "") ?? CardTokenType.UNKNOWN,
          fingerprint: card.fingerprint,
        }),
      ),
      metaData: data.metaData,
    }),
  );

export type JerkCardsResponse = z.infer<typeof JerkCardsResponseSchema>;
